import ContainerForgotPWEmail from "./Components/ContainerForgotPWEmail";
import ContainerForgotPWCode from "./Components/ContainerForgotPWCode";
import ContainerForgotPWReset from "./Components/ContainerForgotPWReset";
import { useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";

const endpoints = {
  request: `${API_BASE_URL}auth/trader/password-reset/request/`,
  verify: `${API_BASE_URL}auth/trader/password-reset/verify/`,
  confirm: `${API_BASE_URL}auth/trader/password-reset/confirm/`,
};

const translateError = (message) => {
  const translations = {
    "Invalid or expired verification code.":
      "کد تأیید نامعتبر است یا منقضی شده است",
    "Invalid or expired reset token.":
      "زمان تغییر رمز عبور منقضی شده است. لطفا دوباره کد دریافت کنید",
    "Verification code could not be queued.":
      "ارسال کد تأیید انجام نشد. لطفا دوباره تلاش کنید",
    "Code must be a 6-digit string.": "کد تأیید باید شش رقمی باشد",
    "Enter a valid email address.": "لطفا یک ایمیل معتبر وارد کنید",
    "Passwords do not match.": "رمزهای عبور مطابقت ندارند",
    "This password is too short. It must contain at least 8 characters.":
      "رمز عبور باید حداقل ۸ کاراکتر داشته باشد",
    "This password is too common.": "رمز عبور انتخاب‌شده بیش از حد ساده است",
    "This password is entirely numeric.": "رمز عبور نمی‌تواند فقط شامل عدد باشد",
  };

  return translations[message] || "خطایی رخ داد. لطفا دوباره تلاش کنید";
};

const getErrorMessage = (data) => {
  if (typeof data?.message === "string") return translateError(data.message);
  const firstError = Object.values(data || {}).flat().find(Boolean);
  return typeof firstError === "string"
    ? translateError(firstError)
    : "ارتباط با سرور ناموفق بود. لطفا دوباره تلاش کنید";
};

const post = async (url, body) => {
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("ارتباط با سرور ناموفق بود. لطفا دوباره تلاش کنید");
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(getErrorMessage(data));
  return data;
};

export default function ForgotPW() {
  const [step, setStep] = useState("email");
  const navigate = useNavigate();
  const [recoveryData, setRecoveryData] = useState({
    email: "",
    resetToken: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative flex justify-center items-center min-h-dvh bg-zi-bg1-light dark:bg-zi-bg1-dark">
      {step === "email" && (
        <ContainerForgotPWEmail
          text="فراموشی رمز عبور"
          loading={loading}
          error={error}
          onInputChange={() => setError("")}
          onContinue={async (email) => {
            setLoading(true);
            setError("");
            try {
              await post(endpoints.request, { email });
              setRecoveryData((previous) => ({ ...previous, email }));
              setStep("code");
            } catch (requestError) {
              setError(requestError.message);
            } finally {
              setLoading(false);
            }
          }}
          onBack={() => navigate("/signin")}
        />
      )}
      {step === "code" && (
        <ContainerForgotPWCode
          email={recoveryData.email}
          loading={loading}
          error={error}
          onInputChange={() => setError("")}
          onBack={() => {
            setError("");
            setStep("email");
          }}
          onResend={async () => {
            await post(endpoints.request, { email: recoveryData.email });
          }}
          onContinue={async (code) => {
            setLoading(true);
            setError("");
            try {
              const result = await post(endpoints.verify, {
                email: recoveryData.email,
                code,
              });
              setRecoveryData((previous) => ({
                ...previous,
                resetToken: result.data.reset_token,
              }));
              setStep("reset");
            } catch (verifyError) {
              setError(verifyError.message);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
      {step === "reset" && (
        <ContainerForgotPWReset
          text="تنظیم مجدد رمز عبور"
          loading={loading}
          error={error}
          onInputChange={() => setError("")}
          onBack={() => {
            setError("");
            setStep("code");
          }}
          onSubmit={async (password) => {
            setLoading(true);
            setError("");
            try {
              await post(endpoints.confirm, {
                reset_token: recoveryData.resetToken,
                new_password: password,
                new_password_again: password,
              });
              navigate("/signin/email", {
                replace: true,
                state: { passwordReset: true },
              });
            } catch (confirmError) {
              setError(confirmError.message);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </div>
  );
}
